import type { ReactNode } from 'react';
import { Button, cn, Input } from '@/core/ui';
import { Copy, CursorClick, Flag, Play, Trash, WarningCircle } from '@/core/ui/icons';
import { blockSpec } from '../../domain/blocks';
import {
  deleteBlock,
  deleteGroup,
  duplicateBlock,
  duplicateGroup,
  edgeSourceGroup,
  findGroup,
  locateBlock,
  removeEdges,
  setStart,
  updateBlock,
  updateGroup,
} from '../../domain/document';
import type { IssueTarget } from '../../domain/issues';
import type { FlowIssue } from '../../domain/types';
import { BlockGlyph } from '../BlockGlyph';
import { CATEGORY_TONE } from '../icons';
import { BlockForm } from './BlockForm';
import { selectedBlockId, selectedEdgeId, selectedGroupId, useBuilder } from './context';
import { IssuesPanel } from './IssuesPanel';
import { VariablesPanel } from './VariablesPanel';

export type PanelTab = 'editar' | 'variaveis' | 'problemas';

interface Props {
  tab: PanelTab;
  onTab: (tab: PanelTab) => void;
  issues: FlowIssue[];
  onFocusIssue: (target: Exclude<IssueTarget, null>) => void;
  onCheck: () => void;
  checking: boolean;
}

/** Painel à direita do canvas: editar a seleção, as variáveis e os problemas. */
export function SidePanel({ tab, onTab, issues, onFocusIssue, onCheck, checking }: Props) {
  const errors = issues.filter((i) => i.severity === 'error').length;
  const tabs: { id: PanelTab; label: ReactNode }[] = [
    { id: 'editar', label: 'Editar' },
    { id: 'variaveis', label: 'Variáveis' },
    {
      id: 'problemas',
      label: (
        <>
          Problemas
          {issues.length > 0 && (
            <span
              className={cn(
                'ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
                errors ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-800',
              )}
            >
              {issues.length}
            </span>
          )}
        </>
      ),
    },
  ];

  return (
    <aside aria-label="Edição" className="flex h-full min-h-0 flex-col">
      <div role="tablist" aria-label="Painel" className="flex gap-1 p-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => onTab(t.id)}
            className={cn(
              'flex flex-1 items-center justify-center rounded-xl px-2 py-1.5 text-sm transition-colors',
              tab === t.id ? 'bg-surface-2 font-medium text-fg' : 'text-fg-subtle hover:text-fg',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div role="tabpanel" className="min-h-0 flex-1 overflow-y-auto px-4 pt-2 pb-6">
        {tab === 'editar' && <SelectionEditor issues={issues} />}
        {tab === 'variaveis' && <VariablesPanel />}
        {tab === 'problemas' && (
          <IssuesPanel issues={issues} onFocus={onFocusIssue} onCheck={onCheck} checking={checking} />
        )}
      </div>
    </aside>
  );
}

function SelectionEditor({ issues }: { issues: FlowIssue[] }) {
  const { doc, selection } = useBuilder();
  const blockId = selectedBlockId(selection);
  const groupId = selectedGroupId(selection);
  const edgeId = selectedEdgeId(selection);

  if (blockId && locateBlock(doc, blockId)) return <BlockEditor blockId={blockId} issues={issues} />;
  if (groupId && findGroup(doc, groupId)) return <CardEditor groupId={groupId} />;
  if (edgeId && doc.edges.some((e) => e.id === edgeId)) return <EdgeEditor edgeId={edgeId} />;
  return (
    <div className="flex flex-col items-center rounded-2xl bg-surface-2 px-5 py-10 text-center">
      <CursorClick aria-hidden className="size-7 text-fg-subtle" />
      <p className="mt-3 text-sm text-fg">Selecione um card ou um bloco para editar.</p>
      <p className="mt-1 text-xs text-fg-subtle">
        Arraste blocos da paleta para o canvas e ligue as saídas (bolinhas à direita) ao início de outro card.
      </p>
    </div>
  );
}

function BlockEditor({ blockId, issues }: { blockId: string; issues: FlowIssue[] }) {
  const { doc, update, select } = useBuilder();
  const at = locateBlock(doc, blockId)!;
  const spec = blockSpec(at.block.type);
  const mine = issues.filter((i) => i.block_id === blockId && !i.edge_id);

  return (
    <div className="space-y-4">
      <header className="flex items-start gap-3">
        <span
          className={cn(
            'flex size-9 shrink-0 items-center justify-center rounded-xl',
            CATEGORY_TONE[spec.category],
          )}
        >
          <BlockGlyph type={at.block.type} className="size-4.5" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-fg">{spec.label}</h2>
          <p className="truncate text-xs text-fg-subtle">
            Bloco {at.index + 1} de “{at.group.title || 'Card sem título'}”
          </p>
        </div>
      </header>

      {mine.length > 0 && (
        <ul className="space-y-1">
          {mine.map((i, n) => (
            <li
              key={n}
              className={cn(
                'flex items-start gap-2 rounded-xl px-3 py-2 text-xs',
                i.severity === 'error' ? 'bg-rose-50 text-rose-800' : 'bg-amber-50 text-amber-900',
              )}
            >
              <WarningCircle aria-hidden weight="fill" className="mt-px size-3.5 shrink-0" />
              {i.message}
            </li>
          ))}
        </ul>
      )}

      <BlockForm block={at.block} onChange={(b) => update((d) => updateBlock(d, b))} />

      <div className="flex gap-2 pt-1">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            const copy = duplicateBlock(doc, blockId);
            update(() => copy.doc);
            select({ groupId: at.group.id, blockId: copy.blockId });
          }}
        >
          <Copy aria-hidden className="size-3.5" />
          Duplicar
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            update((d) => deleteBlock(d, blockId));
            select({ groupId: at.group.id });
          }}
        >
          <Trash aria-hidden className="size-3.5" />
          Apagar bloco
        </Button>
      </div>
    </div>
  );
}

function CardEditor({ groupId }: { groupId: string }) {
  const { doc, update, select, testFrom } = useBuilder();
  const group = findGroup(doc, groupId)!;
  const isStart = doc.start_group_id === groupId;

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <label htmlFor="card-title" className="block text-xs font-medium text-fg-subtle">
          Título do card
        </label>
        <Input
          id="card-title"
          value={group.title}
          maxLength={300}
          placeholder="Card sem título"
          onChange={(e) => update((d) => updateGroup(d, groupId, { title: e.target.value }))}
        />
      </div>
      <p className="text-sm text-fg-muted">
        {group.blocks.length === 0
          ? 'Card vazio. Clique num bloco da paleta para adicionar aqui.'
          : `${group.blocks.length} ${group.blocks.length === 1 ? 'bloco' : 'blocos'}. Clique num bloco do card para editar; clique na paleta para adicionar ao fim.`}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="secondary" onClick={() => testFrom(groupId)}>
          <Play aria-hidden className="size-3.5" />
          Testar a partir daqui
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={isStart}
          onClick={() => update((d) => setStart(d, groupId))}
        >
          <Flag aria-hidden className="size-3.5" />
          {isStart ? 'É o início' : 'Definir como início'}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            const copy = duplicateGroup(doc, groupId);
            update(() => copy.doc);
            select({ groupId: copy.groupId });
          }}
        >
          <Copy aria-hidden className="size-3.5" />
          Duplicar
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            update((d) => deleteGroup(d, groupId));
            select(null);
          }}
        >
          <Trash aria-hidden className="size-3.5" />
          Excluir card
        </Button>
      </div>
    </div>
  );
}

function EdgeEditor({ edgeId }: { edgeId: string }) {
  const { doc, update, select } = useBuilder();
  const edge = doc.edges.find((e) => e.id === edgeId)!;
  const from = edgeSourceGroup(doc, edge);
  const to = findGroup(doc, edge.to_group_id);
  return (
    <div className="space-y-4">
      <h2 className="text-base font-semibold text-fg">Ligação</h2>
      <p className="text-sm text-fg-muted">
        De “{from?.title || 'Card sem título'}” para “{to?.title || 'Card sem título'}”.
      </p>
      <p className="text-xs text-fg-subtle">
        Para trocar o destino, puxe de novo a mesma saída até outro card.
      </p>
      <Button
        size="sm"
        variant="secondary"
        onClick={() => {
          update((d) => removeEdges(d, [edgeId]));
          select(null);
        }}
      >
        <Trash aria-hidden className="size-3.5" />
        Apagar ligação
      </Button>
    </div>
  );
}
